import { Decimal } from "decimal.js";
import {
	AstUtils,
	type IndexManager,
	type MaybePromise,
	type References,
	type ValidationAcceptor,
	type ValidationChecks,
} from "langium";
import type { LangiumServices } from "langium/lsp";
import { BUILTIN_LIBRARY_URI } from "../builtin/index.js";
import { IprotoBuiltinValidator } from "../builtin/validator.js";
import {
	type Alias,
	type Declaration,
	type Directive,
	type DirectiveLocation,
	type DirectiveParameter,
	type Endpoint,
	type Enum,
	type EnumMember,
	type Exception,
	type Field,
	type File,
	type Import,
	type ImportMember,
	type IprotoAstType,
	Module,
	type ReferenceDirective,
	type ReferenceDirectiveArgument,
	type ReferenceType,
	type ReferenceTypeOption,
	type Scalar,
	type ScalarOption,
	type ScalarParameter,
	type Struct,
} from "../generated/ast.js";
import type { IprotoServices } from "../iproto-module.js";

type IprotoValidationChecks = Required<ValidationChecks<IprotoAstType>>;

type IprotoValidation = {
	[K in keyof IprotoValidationChecks as `validate${K}`]?: Exclude<IprotoValidationChecks[K], any[]>;
};

// prettier-ignore
// biome-ignore format: This block should maintain its current formatting
const MESSAGES = {
	AliasOnlyForScalars: () => "Aliases can only be used for scalar types.",
	MixinOnlyStruct: () => "Only struct types can use mixins.",
	OptionsOnlyStructField: () => "Options can only be set on struct fields.",
	ShadowBuiltinDeclaration: (name: string) => `Cannot shadow built-in declaration '${name}'.`,
	DuplicateStructInheritance: () => "A struct cannot appear multiple times in an inheritance chain.",
	CircularInheritance: () => "Circular inheritance detected.",
	OptionsOnlyForScalars: () => "Options can only be set on scalar types.",
	ParametersOnlyForScalarAndStruct: () => "Type parameters can only be used with scalars and structs.",
	DuplicateModule: (name: string) => `Module '${name}' is already defined.`,
	DuplicateIdentifier: (name: string) => `Identifier '${name}' is already defined.`,
	DuplicateImport: (name: string) => `Import '${name}' is already defined.`,
	DuplicateParameter: (name: string) => `Parameter '${name}' is already set.`,
	DuplicateOption: (name: string) => `Option '${name}' is already set.`,
	DuplicateDirectiveLocation: (name: string) => `Directive location '${name}' is already defined.`,
	CircularModuleReference: () => "Circular module references are not allowed.",
	InvalidDirectiveUsage: (directive: string, on: string) => `Directive '#${directive}' cannot be used on '${on}'.`,
	LiteralTypeMismatch: (expected: string) => `Type mismatch: expected '${expected}'.`,
	MissingParameters: (params: string[]) => `Missing required parameter(s): ${params.join(", ")}.`,
	TypeParameterMismatch: (name: string, expected: number, received: number) => `Scalar type '${name}' expects ${expected} parameter(s), but ${received} were provided.`,
	VariadicTypeParameterMismatch: (name: string, min: number, received: number) => `Scalar type '${name}' requires at least ${min} parameter(s), but ${received} were provided.`,
	PrecisionLoss: (value: string) => `Possible precision loss or misinterpretation for value '${value}'.`,
	InvalidInteger: (value: string) => `Invalid integer '${value}': must be in the range [-2147483648, 2147483647].`,
	InputMustBeStruct: () => "Input type must be a struct.",
	OutputMustBeStruct: () => "Output type must be a struct.",
};

const constantCase = (name: string) => {
	return name.toUpperCase();
};

export class IprotoValidator implements IprotoValidation {
	indexManager: IndexManager;
	references: References;
	builtin: IprotoBuiltinValidator;

	constructor(services: LangiumServices) {
		this.indexManager = services.shared.workspace.IndexManager;
		this.references = services.references.References;
		this.builtin = new IprotoBuiltinValidator();
	}

	validateException(node: Exception, accept: ValidationAcceptor): MaybePromise<void> {
		// 不能使用 options
		node.fields.map((field) => {
			if (field.type.options.length > 0) {
				accept("error", MESSAGES.OptionsOnlyStructField(), { node: field.type });
			}
		});
		this.validateFields(node.fields, accept);
	}

	validateDirective(node: Directive, accept: ValidationAcceptor): MaybePromise<void> {
		{
			const visited: DirectiveLocation[] = [];

			for (const element of node.locations) {
				if (visited.includes(element)) {
					accept("error", MESSAGES.DuplicateDirectiveLocation(element), { node });
				} else {
					visited.push(element);
				}
			}
		}

		// 参数名不能重复
		{
			const parameters = new Map<string, DirectiveParameter>();

			for (const param of node.parameters) {
				const exists = parameters.get(constantCase(param.name));

				if (exists) {
					accept("error", MESSAGES.DuplicateIdentifier(param.name), { node: param });
					accept("error", MESSAGES.DuplicateIdentifier(param.name), { node: exists });
				} else {
					parameters.set(constantCase(param.name), param);
				}
			}
		}
	}

	validateEnum(node: Enum, accept: ValidationAcceptor): MaybePromise<void> {
		// 成员不能重复
		{
			const members = new Map<string, EnumMember>();

			for (const member of node.members) {
				const exists = members.get(constantCase(member.name));

				if (exists) {
					accept("error", MESSAGES.DuplicateIdentifier(member.name), { node: member });
					accept("error", MESSAGES.DuplicateIdentifier(member.name), { node: exists });
				} else {
					members.set(constantCase(member.name), member);
				}
			}
		}
	}

	validateEnumMember(node: EnumMember, accept: ValidationAcceptor): MaybePromise<void> {}

	validateImport(node: Import, accept: ValidationAcceptor): MaybePromise<void> {
		// 不能循环依赖
		{
			const visitedDependencies = new Set<Module>();

			const collectDependencyModules = (module: Module) => {
				for (const decl of module.$container.declaraions) {
					if (decl.$type === "Import" && decl.target.ref) {
						const target = decl.target.ref;

						if (!visitedDependencies.has(target)) {
							visitedDependencies.add(target);
							collectDependencyModules(target);
						}
					}
				}
			};

			const target = node.target.ref;

			if (target) {
				visitedDependencies.add(target);
				collectDependencyModules(target);
			}

			if (visitedDependencies.has(node.$container.module)) {
				accept("error", MESSAGES.CircularModuleReference(), { node });
			}
		}

		// 不能重复导入
		{
			const members = new Map<string, ImportMember>();

			for (const member of node.members) {
				const exists = members.get(member.target.$refText);

				if (exists) {
					accept("error", MESSAGES.DuplicateImport(member.target.$refText), { node: member });
					accept("error", MESSAGES.DuplicateImport(member.target.$refText), { node: exists });
				} else {
					members.set(member.target.$refText, member);
				}
			}
		}
	}

	validateModule(node: Module, accept: ValidationAcceptor): MaybePromise<void> {
		// 模块名不能重复
		{
			const modules = this.indexManager
				.allElements(Module)
				.toArray()
				.map((desc) => desc.node);
			const visitedModules = new Map<string, Module>();

			for (const element of modules) {
				if (element) {
					const module = element as Module;
					const uri = AstUtils.getDocument(module).uri.toString();

					if (uri !== BUILTIN_LIBRARY_URI) {
						const exists = visitedModules.get(constantCase(module.name));

						if (exists) {
							accept("error", MESSAGES.DuplicateModule(module.name), { node: exists, property: "name" });
							accept("error", MESSAGES.DuplicateModule(module.name), { node: module, property: "name" });
						} else {
							visitedModules.set(constantCase(module.name), module);
						}
					}
				}
			}
		}
	}

	validateReferenceDirective(node: ReferenceDirective, accept: ValidationAcceptor): MaybePromise<void> {
		const target = node.target.ref;

		if (!target) {
			return;
		}

		// 判断指令是否可用
		{
			const locations = target.locations;

			if (locations.length > 0) {
				const name = target.name;

				switch (node.$container.$type) {
					case "Endpoint": {
						if (!locations.includes("endpoint")) {
							accept("error", MESSAGES.InvalidDirectiveUsage(name, node.$container.$type), { node });
						}

						break;
					}
					case "EnumMember": {
						if (!locations.includes("variant")) {
							accept("error", MESSAGES.InvalidDirectiveUsage(name, node.$container.$type), { node });
						}

						break;
					}
					case "Enum": {
						if (!locations.includes("enum")) {
							accept("error", MESSAGES.InvalidDirectiveUsage(name, node.$container.$type), { node });
						}

						break;
					}
					case "Exception": {
						if (!locations.includes("exception")) {
							accept("error", MESSAGES.InvalidDirectiveUsage(name, node.$container.$type), { node });
						}

						break;
					}
					case "Field": {
						if (!locations.includes("field")) {
							accept("error", MESSAGES.InvalidDirectiveUsage(name, node.$container.$type), { node });
						}

						break;
					}
					case "Module": {
						if (!locations.includes("module")) {
							accept("error", MESSAGES.InvalidDirectiveUsage(name, node.$container.$type), { node });
						}

						break;
					}
					case "Scalar": {
						if (!locations.includes("scalar")) {
							accept("error", MESSAGES.InvalidDirectiveUsage(name, node.$container.$type), { node });
						}

						break;
					}
					case "Struct": {
						if (!locations.includes("struct")) {
							accept("error", MESSAGES.InvalidDirectiveUsage(name, node.$container.$type), { node });
						}

						break;
					}
				}
			}
		}

		// 判断参数是否重复
		{
			const args = new Map<DirectiveParameter, ReferenceDirectiveArgument>();

			for (const argument of node.arguments) {
				const target = argument.target.ref;

				if (target) {
					const exists = args.get(target);
					if (exists) {
						accept("error", MESSAGES.DuplicateParameter(target.name), { node: argument, property: "target" });
						accept("error", MESSAGES.DuplicateParameter(target.name), { node: exists, property: "target" });
					} else {
						args.set(target, argument);
					}
				}
			}
		}

		// 判断是否缺少必要的参数
		{
			const target = node.target.ref;

			if (target) {
				const requiredParameters = target.parameters.filter((param) => !param.optional).map((param) => param.name);
				const requiredParametersSet = new Set(requiredParameters);

				node.arguments.map((arg) => {
					requiredParametersSet.delete(arg.target.$refText);
				});

				if (requiredParametersSet.size > 0) {
					accept("error", MESSAGES.MissingParameters([...requiredParametersSet]), { node, property: "target" });
				}
			}
		}
	}

	validateReferenceDirectiveArgument(node: ReferenceDirectiveArgument, accept: ValidationAcceptor): MaybePromise<void> {
		// 验证参数类型是否正确
		{
			const target = node.target.ref;

			if (target) {
				const expect = target.type;
				const got = node.value?.$type || "BooleanLiteral";

				if (expect === "boolean" && got !== "BooleanLiteral") {
					accept("error", MESSAGES.LiteralTypeMismatch(expect), { node, property: "value" });
				}

				if (expect === "string" && got !== "StringLiteral") {
					accept("error", MESSAGES.LiteralTypeMismatch(expect), { node, property: "value" });
				}

				if (expect === "float" && got !== "NumberLiteral") {
					accept("error", MESSAGES.LiteralTypeMismatch(expect), { node, property: "value" });
				}

				if (expect === "float" && got === "NumberLiteral") {
					let will = false;

					try {
						const decimal = new Decimal(node.value?.value as string);
						const js = Number(node.value?.value as string);
						will = decimal.toString() !== js.toString();
					} catch (e) {
						console.log(e);
						will = true;
					}

					if (will) {
						accept("warning", MESSAGES.PrecisionLoss(node.value?.value as string), { node });
					}
				}

				if (expect === "int" && got !== "NumberLiteral") {
					accept("error", MESSAGES.LiteralTypeMismatch(expect), { node, property: "value" });
				}

				if (expect === "int" && got === "NumberLiteral") {
					const decimal = Decimal(node.value?.value as string);

					if (!decimal.gte(-2147483648) || !decimal.lte(2147483647) || !decimal.isInteger()) {
						accept("error", MESSAGES.InvalidInteger(decimal.toString()), { node, property: "value" });
					}
				}
			}
		}
	}

	validateReferenceTypeOption(node: ReferenceTypeOption, accept: ValidationAcceptor): MaybePromise<void> {
		// 验证选项值类型是否正确
		{
			const target = node.target.ref;

			if (target) {
				const expect = target.type;
				const got = node.value?.$type || "BooleanLiteral";

				if (expect === "boolean" && got !== "BooleanLiteral") {
					accept("error", MESSAGES.LiteralTypeMismatch(expect), { node, property: "value" });
				}

				if (expect === "string" && got !== "StringLiteral") {
					accept("error", MESSAGES.LiteralTypeMismatch(expect), { node, property: "value" });
				}

				if (expect === "float" && got !== "NumberLiteral") {
					accept("error", MESSAGES.LiteralTypeMismatch(expect), { node, property: "value" });
				}

				if (expect === "float" && got === "NumberLiteral") {
					let will = false;

					try {
						const decimal = new Decimal(node.value?.value as string);
						const js = Number(node.value?.value as string);
						will = decimal.toString() !== js.toString();
					} catch (e) {
						console.log(e);
						will = true;
					}

					if (will) {
						accept("warning", MESSAGES.PrecisionLoss(node.value?.value as string), { node });
					}
				}

				if (expect === "int" && got !== "NumberLiteral") {
					accept("error", MESSAGES.LiteralTypeMismatch(expect), { node, property: "value" });
				}

				if (expect === "int" && got === "NumberLiteral") {
					const decimal = Decimal(node.value?.value as string);

					if (!decimal.gte(-2147483648) || !decimal.lte(2147483647) || !decimal.isInteger()) {
						accept("error", MESSAGES.InvalidInteger(decimal.toString()), { node, property: "value" });
					}
				}
			}
		}

		this.builtin.validateReferenceTypeOption(node, accept);
	}

	validateReferenceType(node: ReferenceType, accept: ValidationAcceptor): MaybePromise<void> {
		if (!node.target.ref) {
			return;
		}

		const target = node.target.ref;

		// 验证类型参数数量是否匹配
		if (target.$type === "Scalar") {
			const parametersLength = target?.parameters.length || 0;
			const argumentsLength = node.arguments.length || 0;

			if (!target.ellipsis) {
				if (parametersLength !== argumentsLength) {
					accept("error", MESSAGES.TypeParameterMismatch(target.name, parametersLength, argumentsLength), {
						node,
						property: "target",
					});
				}
			} else {
				if (argumentsLength < parametersLength) {
					accept("error", MESSAGES.VariadicTypeParameterMismatch(target.name, parametersLength, argumentsLength), {
						node,
						property: "target",
					});
				}
			}
		}

		// 仅可为 Scalar 设置引用选项
		if (node.options.length > 0 && target.$type !== "Scalar") {
			accept("error", MESSAGES.OptionsOnlyForScalars(), { node, property: "target" });
		}

		// 仅可为 Scalar 和 Struct 设置类型参数
		if (node.arguments.length > 0) {
			if (target.$type !== "Scalar" && target.$type !== "Struct") {
				accept("error", MESSAGES.ParametersOnlyForScalarAndStruct(), { node, property: "target" });
			}
		}

		// Scalar 选项不能重复设置
		{
			const options = new Map<ScalarOption, ReferenceTypeOption>();

			for (const option of node.options) {
				const target = option.target.ref;

				if (target) {
					const exists = options.get(target);

					if (exists) {
						accept("error", MESSAGES.DuplicateOption(target.name), { node: option, property: "target" });
						accept("error", MESSAGES.DuplicateOption(target.name), { node: exists, property: "target" });
					} else {
						options.set(target, option);
					}
				}
			}
		}

		this.builtin.validateReferenceType(node, accept);
	}

	validateScalar(node: Scalar, accept: ValidationAcceptor): MaybePromise<void> {
		// 参数名不能重复
		{
			const parameters = new Map<string, ScalarParameter>();
			for (const param of node.parameters) {
				const exists = parameters.get(constantCase(param.name));

				if (exists) {
					accept("error", MESSAGES.DuplicateIdentifier(param.name), { node: param });
					accept("error", MESSAGES.DuplicateIdentifier(param.name), { node: exists });
				} else {
					parameters.set(constantCase(param.name), param);
				}
			}
		}

		// 选项名不能重复
		{
			const options = new Map<string, ScalarOption>();
			for (const option of node.options) {
				const exists = options.get(constantCase(option.name));

				if (exists) {
					accept("error", MESSAGES.DuplicateIdentifier(option.name), { node: option, property: "name" });
					accept("error", MESSAGES.DuplicateIdentifier(option.name), { node: exists, property: "name" });
				} else {
					options.set(constantCase(option.name), option);
				}
			}
		}
	}

	validateStruct(node: Struct, accept: ValidationAcceptor): MaybePromise<void> {
		// 只能继承其他 Struct
		for (const element of node.mixins) {
			if (element.target.ref && element.target.ref.$type !== "Struct") {
				accept("error", MESSAGES.MixinOnlyStruct(), { node: element });
			}
		}

		// 在同一个结构体的继承中不能两次出现同一个结构体
		{
			const visited = new Map<Struct, ReferenceType>();

			for (const element of node.mixins) {
				if (element.target.ref && element.target.ref.$type === "Struct") {
					const exists = visited.get(element.target.ref);

					if (exists) {
						accept("error", MESSAGES.DuplicateStructInheritance(), { node: element });
						accept("error", MESSAGES.DuplicateStructInheritance(), { node: exists });
					} else {
						visited.set(element.target.ref, element);
					}
				}
			}
		}

		const inheritedStructs = new Set<Struct>();

		const collectInheritedStruct = (struct: Struct) => {
			for (const element of struct.mixins) {
				if (element.target.ref && element.target.ref.$type === "Struct") {
					const target = element.target.ref;
					if (!inheritedStructs.has(target)) {
						inheritedStructs.add(target);
						collectInheritedStruct(target);
					}
				}
			}
		};

		collectInheritedStruct(node);

		// 不能在 Mixins 中循环引用
		if (inheritedStructs.has(node)) {
			accept("error", MESSAGES.CircularInheritance(), { node, property: "name" });
		}

		this.validateFields(node.fields, accept);
	}

	validateAlias(node: Alias, accept: ValidationAcceptor): MaybePromise<void> {
		const type = node.type;

		if (type.target.ref && type.target.ref.$type !== "Scalar") {
			accept("error", MESSAGES.AliasOnlyForScalars(), { node, property: "type" });
		}
	}

	validateEndpoint(node: Endpoint, accept: ValidationAcceptor): MaybePromise<void> {
		if (node.input) {
			const target = node.input.target.ref;

			if (target?.$type !== "Struct") {
				accept("error", MESSAGES.InputMustBeStruct(), { node, property: "input" });
			}
		}

		if (node.output) {
			const target = node.output.target.ref;

			if (target?.$type !== "Struct") {
				accept("error", MESSAGES.OutputMustBeStruct(), { node, property: "output" });
			}
		}
	}

	validateField(node: Field, accept: ValidationAcceptor): MaybePromise<void> {}

	validateFields(nodes: Field[], accept: ValidationAcceptor): MaybePromise<void> {
		// 字段不能重复
		const fields = new Map<string, Field>();

		for (const field of nodes) {
			const exists = fields.get(constantCase(field.name));

			if (exists) {
				accept("error", MESSAGES.DuplicateIdentifier(field.name), { node: field });
				accept("error", MESSAGES.DuplicateIdentifier(field.name), { node: exists });
			} else {
				fields.set(constantCase(field.name), field);
			}
		}
	}

	validateFile(node: File, accept: ValidationAcceptor): MaybePromise<void> {
		const documentUri = AstUtils.getDocument(node).uri.toString();

		if (documentUri === BUILTIN_LIBRARY_URI) {
			return;
		}

		// 顶级声明标识符不能重复
		{
			const visited = new Map<string, Declaration | ImportMember>();
			const builtinDeclarations = new Map<string, Declaration>();

			for (const decl of this.getBuiltinDeclarations()) {
				if (decl.$type !== "Import") {
					builtinDeclarations.set(constantCase(decl.name), decl);
				}
			}

			const declarations: Array<Declaration> = node.declaraions;
			const importMembers: Array<ImportMember> = [];

			for (const decl of [...declarations]) {
				if (decl.$type === "Import") {
					importMembers.push(...decl.members);
				}
			}

			for (const decl of [...declarations, ...importMembers]) {
				if (decl.$type === "Import") {
					continue;
				}

				let name: string;

				if (decl.$type === "ImportMember") {
					name = decl.name || decl.target.$refText;
				} else {
					name = decl.name;
				}

				if (builtinDeclarations.has(constantCase(name))) {
					accept("error", MESSAGES.ShadowBuiltinDeclaration(name), { node: decl, property: "name" });
				} else {
					const exists = visited.get(constantCase(name));

					if (exists) {
						const message = MESSAGES.DuplicateIdentifier(name);

						if (exists.$type === "ImportMember") {
							accept("error", message, { node: exists, property: exists.name ? "name" : "target" });
						} else {
							accept("error", message, { node: exists, property: "name" });
						}

						accept("error", message, { node: decl, property: "name" });
					} else {
						visited.set(constantCase(name), decl);
					}
				}
			}
		}
	}

	private getBuiltinDeclarations() {
		const descriptions = this.indexManager.allElements(Module, new Set([BUILTIN_LIBRARY_URI])).toArray();

		if (descriptions[0]?.node) {
			const module = descriptions[0].node as Module;
			const declaraions = module.$container.declaraions;

			return declaraions;
		}

		return [];
	}

	protected checks(): IprotoValidationChecks {
		return {
			Alias: this.validateAlias,
			BooleanLiteral: [],
			Declaration: [],
			Directive: this.validateDirective,
			DirectiveParameter: [],
			Enum: this.validateEnum,
			EnumMember: this.validateEnumMember,
			Import: this.validateImport,
			ImportMember: [],
			Importable: [],
			NumberLiteral: [],
			LiteralValue: [],
			Module: this.validateModule,
			ReferenceDirective: this.validateReferenceDirective,
			ReferenceDirectiveArgument: this.validateReferenceDirectiveArgument,
			ReferenceTypeOption: this.validateReferenceTypeOption,
			ReferenceType: this.validateReferenceType,
			Scalar: this.validateScalar,
			ScalarOption: [],
			ScalarParameter: [],
			Endpoint: this.validateEndpoint,
			StringLiteral: [],
			Struct: this.validateStruct,
			Field: this.validateField,
			Type: [],
			AstNode: [],
			File: this.validateFile,
			Exception: this.validateException,
		};
	}

	register(services: IprotoServices) {
		const registry = services.validation.ValidationRegistry;
		const validator = services.validation.IprotoValidator;

		registry.register(validator.checks(), validator);
	}

	static mergeValidationChecks(...validators: IprotoValidationChecks[]) {
		const checks: {
			// biome-ignore lint/complexity/noBannedTypes: <explanation>
			[K in keyof IprotoValidationChecks]: Exclude<IprotoValidationChecks[K], Function>;
		} = {
			Alias: [],
			BooleanLiteral: [],
			Declaration: [],
			Directive: [],
			DirectiveParameter: [],
			Enum: [],
			EnumMember: [],
			Exception: [],
			Field: [],
			File: [],
			Import: [],
			LiteralValue: [],
			NumberLiteral: [],
			Module: [],
			ReferenceDirective: [],
			ReferenceDirectiveArgument: [],
			ReferenceType: [],
			ReferenceTypeOption: [],
			Scalar: [],
			ScalarOption: [],
			ScalarParameter: [],
			Endpoint: [],
			StringLiteral: [],
			Struct: [],
			Type: [],
			AstNode: [],
			ImportMember: [],
			Importable: [],
		};

		const keys = Object.keys(checks) as Array<keyof IprotoValidationChecks>;

		for (const validator of validators) {
			for (const key of keys) {
				const item = validator[key];

				if (item) {
					// @ts-ignore
					checks[key].push(...(Array.isArray(item) ? item : [item]));
				}
			}
		}

		return checks;
	}
}
